import { useEffect, useRef, useState } from 'react'

/**
 * Noise-displaced sphere drawn as ~26,000 dots, lit by a cursor-tracked point
 * light. Raw WebGL on purpose: three.js would be a ~600 KB dependency for one
 * decorative canvas on the auth pages.
 *
 * Ported from the marketing prototype (signup-v2, variant "generative matter").
 * When WebGL is unavailable it renders `.gart-fallback` instead — a static radial
 * glow — so the panel never reads as an empty black column.
 */

const COUNT = 26000

/** Deterministic PRNG so the stipple is identical on every reload. */
const rngFrom = (seed: number) => {
  let v = seed >>> 0
  return () => {
    v ^= v << 13
    v ^= v >>> 17
    v ^= v << 5
    return (v >>> 0) / 4294967296
  }
}

/**
 * Golden-angle spiral on the unit sphere, jittered by a fraction of the mean
 * point spacing — the bare lattice draws visible curved arcs that read as
 * contour lines rather than as dots.
 */
const fibSphere = (n: number) => {
  const out = new Float32Array(n * 3)
  const ga = Math.PI * (3 - Math.sqrt(5))
  const rnd = rngFrom(0x9e3779b9)
  const jit = 0.42 * Math.sqrt((4 * Math.PI) / n)
  for (let i = 0; i < n; i++) {
    const y0 = 1 - (i / (n - 1)) * 2
    const r = Math.sqrt(Math.max(0, 1 - y0 * y0))
    const th = ga * i
    const x = Math.cos(th) * r + (rnd() - 0.5) * jit
    const y = y0 + (rnd() - 0.5) * jit
    const z = Math.sin(th) * r + (rnd() - 0.5) * jit
    // inputs are near unit length, so hypot's overflow-safe scaling never engages
    const l = Math.sqrt(x * x + y * y + z * z) || 1
    out[i * 3] = x / l
    out[i * 3 + 1] = y / l
    out[i * 3 + 2] = z / l
  }
  return out
}

/* uHover/uCursor are mediump in both stages — a precision mismatch breaks linking. */
const VS = `
attribute vec3 aPos;
uniform mat4 uProj,uView,uModel; uniform float uTime,uAmp,uSize,uDpr;
uniform mediump float uHover; uniform mediump vec3 uCursor;
varying mediump vec3 vN; varying mediump vec3 vP;
vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
float snoise(vec3 v){
  const vec2 C=vec2(1.0/6.0,1.0/3.0); const vec4 D=vec4(0.0,0.5,1.0,2.0);
  vec3 i=floor(v+dot(v,C.yyy)); vec3 x0=v-i+dot(i,C.xxx);
  vec3 g=step(x0.yzx,x0.xyz); vec3 l=1.0-g;
  vec3 i1=min(g.xyz,l.zxy); vec3 i2=max(g.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx; vec3 x2=x0-i2+C.yyy; vec3 x3=x0-D.yyy;
  i=mod289(i);
  vec4 p=permute(permute(permute(i.z+vec4(0.0,i1.z,i2.z,1.0))+i.y+vec4(0.0,i1.y,i2.y,1.0))+i.x+vec4(0.0,i1.x,i2.x,1.0));
  float n_=0.142857142857; vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.0*floor(p*ns.z*ns.z); vec4 x_=floor(j*ns.z); vec4 y_=floor(j-7.0*x_);
  vec4 x=x_*ns.x+ns.yyyy; vec4 y=y_*ns.x+ns.yyyy; vec4 h=1.0-abs(x)-abs(y);
  vec4 b0=vec4(x.xy,y.xy); vec4 b1=vec4(x.zw,y.zw);
  vec4 s0=floor(b0)*2.0+1.0; vec4 s1=floor(b1)*2.0+1.0; vec4 sh=-step(h,vec4(0.0));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy; vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x); vec3 p1=vec3(a0.zw,h.y); vec3 p2=vec3(a1.xy,h.z); vec3 p3=vec3(a1.zw,h.w);
  vec4 nm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=nm.x; p1*=nm.y; p2*=nm.z; p3*=nm.w;
  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0); m=m*m;
  return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}
void main(){
  vec3 n=normalize(aPos);
  float d=snoise(aPos*1.7+uTime*0.28)*uAmp;
  vec3 pos=aPos+n*d;
  float cd=length(pos.xy-uCursor.xy);
  float bump=exp(-cd*cd*2.0)*uHover*0.42;
  pos+=n*bump; vN=n; vP=pos;
  vec4 mv=uView*uModel*vec4(pos,1.0);
  gl_Position=uProj*mv;
  // perspective-correct dot size keeps the sphere reading as a volume
  gl_PointSize=max(1.0,uSize*uDpr/max(-mv.z,0.25));
}`

const FS = `
precision mediump float;
uniform vec3 uA,uB,uLight,uGlow; uniform float uAlpha,uFres,uBase;
uniform mediump vec3 uCursor; uniform mediump float uHover;
varying mediump vec3 vN; varying mediump vec3 vP;
void main(){
  vec3 n=normalize(vN);
  vec3 ld=normalize(uLight-vP);
  float diff=max(dot(n,ld),0.0);
  float fres=pow(1.0-abs(dot(n,vec3(0.0,0.0,1.0))),2.0);
  vec3 base=uA*(uBase+diff*0.55);
  float g=clamp(0.5-vP.y*0.42,0.0,1.0);
  g=mix(g,min(1.0,g+fres*0.35),uFres);
  vec3 col=mix(base,uB,g);
  float cd=length(vP.xy-uCursor.xy);
  float bloom=exp(-cd*cd*2.4)*uHover;
  col=mix(col,uGlow,clamp(bloom*0.9,0.0,1.0));
  // discard rather than fade out: depth testing is on, so a transparent corner
  // would still write depth and punch holes in whatever is behind it
  vec2 pc=gl_PointCoord-vec2(0.5);
  float rr=dot(pc,pc)*4.0;
  if(rr>1.0) discard;
  gl_FragColor=vec4(col,uAlpha*smoothstep(1.0,0.45,rr));
}`

const persp = (f: number, a: number, n: number, fr: number) => {
  const t = 1 / Math.tan(f / 2)
  // biome-ignore format: matrix layout
  return new Float32Array([t / a, 0, 0, 0, 0, t, 0, 0, 0, 0, (fr + n) / (n - fr), -1, 0, 0, (2 * fr * n) / (n - fr), 0])
}

const trans = (z: number) => {
  const m = new Float32Array(16)
  m[0] = m[5] = m[10] = m[15] = 1
  m[14] = z
  return m
}

const rot = (ax: number, ay: number) => {
  const ca = Math.cos(ax)
  const sa = Math.sin(ax)
  const cb = Math.cos(ay)
  const sb = Math.sin(ay)
  // biome-ignore format: matrix layout
  return new Float32Array([cb, 0, -sb, 0, sa * sb, ca, sa * cb, 0, ca * sb, -sa, ca * cb, 0, 0, 0, 0, 1])
}

export const GenerativeMatter = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const cv = canvasRef.current
    // The panel is the event host, not the canvas: `.gart` sits at z-index 0 under
    // rows at z-index 2, so a pointer event can never originate on the canvas.
    const host = cv?.parentElement
    if (!cv || !host) {
      return
    }

    const gl = cv.getContext('webgl', { antialias: true, alpha: true, premultipliedAlpha: false })
    if (!gl) {
      setFailed(true)
      return
    }
    const lose = () => gl.getExtension('WEBGL_lose_context')?.loseContext()

    const compile = (type: number, src: string) => {
      const s = gl.createShader(type) as WebGLShader
      gl.shaderSource(s, src)
      gl.compileShader(s)
      return s
    }
    const prog = gl.createProgram() as WebGLProgram
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VS))
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FS))
    gl.linkProgram(prog)
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      lose()
      setFailed(true)
      return
    }
    gl.useProgram(prog)

    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer())
    gl.bufferData(gl.ARRAY_BUFFER, fibSphere(COUNT), gl.STATIC_DRAW)
    const aPos = gl.getAttribLocation(prog, 'aPos')
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 0, 0)

    const at = (name: string) => gl.getUniformLocation(prog, name)

    /* Uploaded once. Palette is brand blue at the top shading to black at the
       bottom; fresnel is held low so the rim reads as depth rather than glow.
       The view matrix is a fixed pull-back. */
    gl.uniform1f(at('uAmp'), 0.22)
    gl.uniform1f(at('uSize'), 7.2)
    gl.uniform1f(at('uAlpha'), 0.84)
    gl.uniform1f(at('uFres'), 0.3)
    gl.uniform1f(at('uBase'), 1.0)
    gl.uniform3fv(at('uA'), [0.33, 0.39, 1.0])
    gl.uniform3fv(at('uB'), [0, 0, 0])
    gl.uniform3fv(at('uGlow'), [0.86, 0.9, 1.0])
    gl.uniformMatrix4fv(at('uView'), false, trans(-3.35))
    gl.clearColor(0, 0, 0, 0)

    // only these change after setup — uProj/uDpr on resize, the rest per frame
    const U = {
      proj: at('uProj'),
      dpr: at('uDpr'),
      model: at('uModel'),
      time: at('uTime'),
      cursor: at('uCursor'),
      hover: at('uHover'),
      light: at('uLight'),
    }

    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches
    // pointer in clip space; the cursor bulge is eased so it trails the pointer
    let px = 0
    let py = 0
    let hoverTarget = 0
    let hover = 0
    const cursor = new Float32Array(3)
    const light = new Float32Array([0, 0, 4.2])

    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
    // without depth testing the far half of the sphere paints over the near half
    // in buffer order and the volume collapses
    gl.enable(gl.DEPTH_TEST)

    const draw = (t: number) => {
      cursor[0] += (px * 1.55 - cursor[0]) * 0.12 // mesh spans about +/-1.4
      cursor[1] += (py * 1.55 - cursor[1]) * 0.12
      hover += (hoverTarget - hover) * 0.07
      light[0] = px * 3.2
      light[1] = py * 3.2
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
      gl.uniformMatrix4fv(U.model, false, rot(t * 0.04, t * 0.093))
      gl.uniform1f(U.time, t)
      gl.uniform3fv(U.cursor, cursor)
      gl.uniform1f(U.hover, hover)
      gl.uniform3fv(U.light, light)
      gl.drawArrays(gl.POINTS, 0, COUNT)
    }

    const t0 = performance.now()
    let raf = 0
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame)
      draw((now - t0) / 1000)
    }

    /* The canvas is `position:absolute; inset:0`, so it has no box whenever the
       panel is hidden — which is every viewport under `lg`, and every auth page
       that renders the desktop branch on a phone. Observing it rather than
       polling `getBoundingClientRect()` in the frame is what keeps a 60fps loop
       from drawing zero pixels there; the observer fires again with a real box
       the moment one appears. Under reduced motion nothing animates, so a resize
       is the ONLY reason to redraw and no loop is started at all.
       Known gap: this does not catch a devicePixelRatio change with no box
       change (dragging the window to a different-density monitor). */
    const observer = new ResizeObserver(([entry]) => {
      const dpr = Math.min(devicePixelRatio || 1, 2)
      const box = entry.contentRect
      const w = Math.max(1, Math.round(box.width * dpr))
      const h = Math.max(1, Math.round(box.height * dpr))
      if (cv.width !== w || cv.height !== h) {
        cv.width = w
        cv.height = h
        gl.viewport(0, 0, w, h)
        gl.uniformMatrix4fv(U.proj, false, persp(1.15, w / h, 0.1, 100))
        gl.uniform1f(U.dpr, dpr)
      }
      if (box.width < 1 || box.height < 1) {
        cancelAnimationFrame(raf)
        raf = 0
        return
      }
      if (reduce) {
        draw(2.4)
      } else if (!raf) {
        raf = requestAnimationFrame(frame)
      }
    })
    observer.observe(cv)

    // no listeners under reduced motion: uHover stays 0, so both shader effects
    // that read the cursor multiply out to nothing
    const onMove = (e: MouseEvent) => {
      const r = cv.getBoundingClientRect()
      px = ((e.clientX - r.left) / r.width) * 2 - 1
      py = -(((e.clientY - r.top) / r.height) * 2 - 1)
      hoverTarget = 1
    }
    const onLeave = () => {
      hoverTarget = 0
    }
    if (!reduce) {
      host.addEventListener('mousemove', onMove)
      host.addEventListener('mouseleave', onLeave)
    }

    return () => {
      observer.disconnect()
      cancelAnimationFrame(raf)
      host.removeEventListener('mousemove', onMove)
      host.removeEventListener('mouseleave', onLeave)
      // drops the program, shaders and buffer with the context. NOTE: this makes
      // the effect single-shot — under StrictMode's double-invoke the re-run
      // would get the same, now-lost, context back and render nothing.
      lose()
    }
  }, [])

  if (failed) {
    return <div className="gart-fallback" aria-hidden="true" />
  }
  return <canvas ref={canvasRef} className="gart" />
}
